import React, { useEffect, useState, useRef } from 'react';
import Page from '@/components/Page';
import TableHeader from '@/components/TableHeader';
import Card from '@/components/Card';
import Table from '@/components/Table';
import { TableRow, TableCell } from '@/elements/Table';
import TextStack from '@/components/TextStack';
import Stack from '@/components/Stack';
import Link from '@/elements/Link';
import { Body } from '@/components/Typography';
import { Worker } from '@/types/graphql';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { format, subWeeks, startOfWeek } from 'date-fns';
import DateRangeInput from '@/components/DateRangeInput';
import { DateContext } from '../CreateGigOrder/JobEditor/Steps/ScheduleStep/ScheduleSection/DateRangeInput';
import { CSVLink } from 'react-csv';
import { formatDateTime } from '@/util/datetime';
import { EXTERNAL_URL } from '@/util/constants';
import Config from '@/config';

const PayrollAudit = () => {
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [loading, setLoading] = useState(false);
  const [csvData, setCsvData] = useState<any[]>([]);
  const csvInstance = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  // Default start date: 3 Mondays ago
  const defaultStartDate = startOfWeek(subWeeks(new Date(), 2), {
    weekStartsOn: 2,
  });
  // Default end date: current date
  const defaultEndDate = new Date();

  const [dateRange, setDateRange] = useState({
    from: defaultStartDate,
    to: defaultEndDate,
  });

  const fetchWorkers = async (start?: Date | null, end?: Date | null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();

      if (start) {
        params.append('start_date', format(start, 'yyyy-MM-dd'));
      }

      if (end) {
        params.append('end_date', format(end, 'yyyy-MM-dd'));
      }

      const queryString = params.toString();
      const url = `/api/workers/payroll_audit${queryString ? `?${queryString}` : ''}`;

      const res = await axiosRubyClient.get(url);
      setWorkers(res.data);

      // Prepare CSV data
      const csvData = res.data.map((worker: any) => ({
        id: worker.check_employee_id,
        name: `${worker.user.first_name || ''} ${worker.user.middle_name || ''} ${worker.user.last_name || ''}`.trim(),
        email: worker.user.email,
        payment_method: worker.payment_method_preference,
        hired_at: worker.job?.hired_at
          ? formatDateTime(worker.job.hired_at)
          : '',
      }));

      setCsvData(csvData);
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial fetch with default dates
    fetchWorkers(dateRange.from, dateRange.to);
  }, []);

  // Apply filter whenever dateRange changes
  useEffect(() => {
    fetchWorkers(dateRange.from, dateRange.to);
  }, [dateRange]);

  const handleRangeChange = (date: Date, context: DateContext) => {
    let dateString;
    try {
      dateString = date.toISOString();
    } catch {}
    if (context === 'startDate') {
      setDateRange({
        ...dateRange,
        from: dateString,
      });
    }
    if (context === 'endDate') {
      setDateRange({
        ...dateRange,
        to: dateString,
      });
    }
  };

  const handleDownloadCSV = () => {
    if (csvInstance.current) {
      csvInstance.current.link.click();
    }
  };

  // CSV headers
  const headers = [
    { label: 'ID', key: 'id' },
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Payment Method', key: 'payment_method' },
    { label: 'Hired At', key: 'hired_at' },
  ];

  // Generate filename with current date
  const filename = `payrol-audit-${format(new Date(), 'yyyy-MM-dd')}.csv`;

  const checkEmployeeIdUrl = (worker) => {
    let url: string =
      worker.tax_type === 'w2' ? EXTERNAL_URL.w2 : EXTERNAL_URL[1099];

    url = url
      .replace('{0}', Config.CHECKHQ_COMPANY)
      .replace('{1}', worker.check_employee_id);

    return url;
  };

  return (
    <Page
      primaryAction={{
        a11yLabel: 'Download CSV',
        label: 'Download CSV',
        onAction: handleDownloadCSV,
      }}
      size="full"
      title="GravyWorkers"
    >
      <Card>
        <Card.Section>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <DateRangeInput
              noLabels
              endDate={dateRange.to}
              hasPlaceholder={false}
              minDate={dateRange.from}
              startDate={dateRange.from}
              onChange={handleRangeChange}
            />
          </Stack>
        </Card.Section>
        <Table>
          <TableHeader
            fields={['ID', 'Name', 'Email', 'Payment method', 'Hired At']}
          />
          <tbody>
            {workers.map(
              (item) =>
                item &&
                item.user && (
                  <TableRow key={item.id}>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          <Link
                            id={`link-${item.id}`}
                            to={checkEmployeeIdUrl(item)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Body color={'theme'} weight="medium">
                              {item.check_employee_id}
                            </Body>
                          </Link>
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack gap={20}>
                        <TextStack spacing="tight">
                          <Link
                            id={`link-${item.id}`}
                            to={`/workers/${item.id}`}
                          >
                            <Body color={'theme'} weight="medium">
                              {item.user.first_name} {item.user.middle_name}{' '}
                              {item.user.last_name}
                            </Body>
                          </Link>
                        </TextStack>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <TextStack spacing="tight">
                        <Body
                          color={item.active ? 'inherit' : 'lightest'}
                          weight="medium"
                        >
                          {item.user.email}
                        </Body>
                      </TextStack>
                    </TableCell>
                    <TableCell>
                      <TextStack spacing="tight">
                        <Body
                          color={item.active ? 'inherit' : 'lightest'}
                          weight="medium"
                        >
                          {item.payment_method_preference}
                        </Body>
                      </TextStack>
                    </TableCell>
                    <TableCell>
                      <TextStack spacing="tight">
                        <Body
                          color={item.active ? 'inherit' : 'lightest'}
                          weight="medium"
                        >
                          {formatDateTime(item.job.hired_at)}
                        </Body>
                      </TextStack>
                    </TableCell>
                  </TableRow>
                ),
            )}
            {workers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                  <Body>No workers found for the selected date range.</Body>
                </TableCell>
              </TableRow>
            )}
          </tbody>
        </Table>
        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Body>Loading...</Body>
          </div>
        )}
      </Card>

      {/* Hidden CSVLink component */}
      <CSVLink
        ref={csvInstance}
        data={csvData}
        filename={filename}
        headers={headers}
        style={{ display: 'none' }}
      />
    </Page>
  );
};

export default PayrollAudit;
